import { GroupBase, StylesConfig } from 'react-select';
import Option from '@/components/form/types/Option.ts';

/**
 * `toolbar` istnieje dla paska narzędzi tabeli i tego, co pasek otwiera —
 * tam wszystko chodzi na 30 px / 12 px. Poza panelem filtrów zostaje
 * `default`, bo 34 px to normalna wysokość pola formularza.
 */
export type SelectControlSize = 'default' | 'toolbar';

const sizeTokens = {
  default: {
    minHeight: '34px',
    fontSize: '14px',
    valueContainerPadding: '2px 0 2px 12px',
    indicatorPadding: '6px 8px',
  },
  toolbar: {
    minHeight: '30px',
    fontSize: '12px',
    valueContainerPadding: '1px 0 1px 8px',
    indicatorPadding: '3px 6px',
  },
} as const;

/**
 * Menu w arkuszu (`useMobileSelectMenu`): lista ma wypełnić arkusz i sama się
 * przewijać, więc znika ramka, cień i limit wysokości liczony pod polem, a
 * opcje rosną do rozmiaru pod kciuk.
 */
const mobileMenuStyles = <IsMulti extends boolean>(): Pick<
  StylesConfig<Option, IsMulti, GroupBase<Option>>,
  'menu' | 'menuList' | 'option'
> => ({
  menu: (provided) => ({
    ...provided,
    position: 'static',
    margin: 0,
    border: 'none',
    borderRadius: 0,
    boxShadow: 'none',
    backgroundColor: 'transparent',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 'none',
    flex: 1,
    minHeight: 0,
    overflowX: 'hidden',
    overflowY: 'auto',
    border: 'none',
    borderRadius: 0,
    backgroundColor: 'transparent',
    padding: '0 8px',
  }),
  option: (provided) => ({
    ...provided,
    overflowX: 'hidden',
    backgroundColor: 'transparent',
    padding: '12px',
    fontSize: '15px',
    color: '#2E2E38',
    borderRadius: '6px',
    cursor: 'pointer',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    boxSizing: 'border-box',
    '&:active': {
      backgroundColor: '#F0F0F5',
    },
  }),
});

export const getSelectStyles = <IsMulti extends boolean>(
  error?: string,
  size: SelectControlSize = 'default',
  options?: { mobileMenu?: boolean },
): StylesConfig<Option, IsMulti, GroupBase<Option>> => {
  const tokens = sizeTokens[size];

  return {
    input: (provided) => ({
      ...provided,
      fontSize: tokens.fontSize,
      lineHeight: '100%',
      cursor: 'pointer',
      color: '#2E2E38',
      margin: '0',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#C4C4D4',
      fontSize: tokens.fontSize,
      lineHeight: '100%',
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: tokens.valueContainerPadding,
      gap: '2px',
    }),
    control: (provided, state) => ({
      ...provided,
      borderRadius: '6px',
      backgroundColor: state.isDisabled ? '#EEF2F6' : 'white',
      borderColor: error ? '#F5D6D6' : '#E1E1E9',
      borderWidth: state.isFocused ? '0.5px' : provided.borderWidth,
      boxShadow: state.isFocused ? '' : '',
      '&:hover': {
        borderColor: error ? '#F5D6D6' : '#E1E1E9',
      },
      fontSize: tokens.fontSize,
      minHeight: tokens.minHeight,
    }),
    menu: (provided) => ({
      ...provided,
      border: 'none',
      boxShadow: '2px 2px 8px rgba(46, 46, 56, 0.1)',
    }),
    menuList: (provided) => ({
      ...provided,
      overflowX: 'hidden',
      backgroundColor: 'white',
      border: 'solid 1px #E1E1E9',
      borderRadius: '6px',
      padding: '6px',
    }),
    option: (provided) => ({
      ...provided,
      overflowX: 'hidden',
      backgroundColor: 'white',
      padding: '6px 8px',
      color: '#2E2E38',
      borderRadius: '4px',
      cursor: 'pointer',
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      boxSizing: 'border-box',
      '&:hover': {
        backgroundColor: '#F0F0F5',
      },
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: tokens.indicatorPadding,
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: tokens.indicatorPadding,
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    loadingMessage: (provided) => ({
      ...provided,
      padding: 0,
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    ...(options?.mobileMenu ? mobileMenuStyles<IsMulti>() : {}),
  };
};
